// Put-Away (Plassering) related types - mirroring picking types structure
export interface StcIntRelOrderDto {
  id: number;
  companyNo: string;
  warehouseID: string;
  stcOrderNo: number;
  transDt: string;
  orderStatus: number;
  transType: number;
  operatorID?: string;
  operatorGroupID?: string;
  orderPriority: number;
  description1?: string;
  lockStatus: number;
  lockedBy?: string;
  sessionID?: string;
  externalSystemID?: string;
  created: string;
  lastUpdate?: string;
  createdBy?: string;
  updatedBy?: string;
  statusText: string;
  transTypeText: string;
  lineCount: number;
  isLocked: boolean;
  /** Innkjøpsreferanser (TW 36403719): distinkte ERP-innkjøpsnr bak linjene, kommaseparert. Kun i listekall. */
  pOrderNoERPs?: string | null;
  /** Distinkte leverandørnavn bak linjene, kommaseparert. */
  supplierNames?: string | null;
  /** Innkjøper-referanser (OurRef/ContactName), ofte tom. */
  purchaserRefs?: string | null;
}

export interface StcIntRelOrderLineDto {
  id: number;
  companyNo: string;
  warehouseID: string;
  stcOrderNo: number;
  stcLineNo: number;
  itemNo: string;
  quantity: number;
  fromBinCode?: string;
  toBinCode?: string;
  grShipmentNo?: number;
  stcBoxNo?: number;
  lineStatus: number;
  movedBy?: string;
  itemGrp1?: string;
  itemGrp2?: string;
  itemGrp3?: string;
  description1?: string;
  resourceNo?: number;
  stcZoneID?: string;
  transType: number;
  clientID?: string;
  quantityMoved?: number;
  itemBarCode?: string;
  externalSystemID?: string;
  externalSystemStatus?: string;
  resID?: number;
  binGrpNo?: number;
  sorting?: number;
  relID?: number;
  created: string;
  lastUpdate?: string;
  createdBy?: string;
  updatedBy?: string;
  statusText: string;
  transTypeText: string;
  remainingQuantity: number;
  isCompleted: boolean;
  // Unntaksflagg skannekrav (TW 36344429, fra Items/StcBin(toBinCode)):
  // null/undefined = arv fra profil, true/false = overstyr profilkravet for denne linjen.
  barCodeConfirmItem?: boolean | null;
  barCodeConfirmLocation?: boolean | null;
}

export interface StcIntRelOrderDetailsDto {
  header: StcIntRelOrderDto;
  lines: StcIntRelOrderLineDto[];
  totalLines: number;
  completedLines: number;
  totalQuantity: number;
  movedQuantity: number;
  completionPercentage: number;
}

export interface UpdateQuantityMovedRequest {
  quantityMoved: number;
  movedBy?: string;
}

export interface UpdateStcIntRelOrderStatusRequest {
  status: number;
}

export interface LockStcIntRelOrderRequest {
  operatorID?: string;
}

/**
 * Request-body for PUT intrelorders/{stcOrderNo} (speiler CreateStcIntRelOrderRequest i Shared).
 * Endepunktet overskriver ALLE feltene under — send alltid eksisterende verdier tilbake
 * for det som ikke skal endres (se putAwayService.updatePutAwayOrderPriority).
 */
export interface UpdateStcIntRelOrderRequest {
  warehouseID: string;
  stcOrderNo: number;
  transDt?: string;
  orderStatus: number;
  transType: number;
  operatorID?: string;
  operatorGroupID?: string;
  orderPriority: number;
  description1?: string;
  sessionID?: string;
  externalSystemID?: number;
}

/**
 * Plasseringsordrelinje med ordrehodet vedlagt. Brukes av planleggerens
 * «Ordrelinjer»-visning som viser linjer på tvers av ordre.
 */
export interface StcIntRelOrderLineWithHeaderDto extends StcIntRelOrderLineDto {
  header: StcIntRelOrderDto;
}

/**
 * Ordrestatus for StcIntRelOrder (StatusType 26).
 * 0 og 10 er «nye» (ikke godkjent), 20 er godkjent og synlig for operatør, 30 er avsluttet.
 * Regelmotoren bruker i tillegg 100 som midlertidig arbeidsstatus under generering.
 */
export const StcIntRelOrderStatus = {
  MANUELL: 0,
  SYSTEMGENERERT: 10,
  GODKJENT: 20,
  AVSLUTTET: 30,
} as const;

export type StcIntRelOrderStatusValue = typeof StcIntRelOrderStatus[keyof typeof StcIntRelOrderStatus];

/** Ordrestatuser som teller som «ny plasseringsordre» i planleggeren. */
export const NEW_PUTAWAY_ORDER_STATUSES: readonly number[] = [
  StcIntRelOrderStatus.MANUELL,
  StcIntRelOrderStatus.SYSTEMGENERERT,
];

/** Plassholder-lokasjon satt av regelmotoren når ingen mål-lokasjon ble funnet. */
export const PUTAWAY_UNRESOLVED_BIN = '[X]';

/**
 * Transaksjonstype for StcIntRelOrder / StcIntRelOrderLine (StatusType 25).
 */
export const StcIntRelOrderTransType = {
  UTLEGG: 1,
  PAFYLLING: 2,
  OPTIMALISERING: 3,
} as const;

export type StcIntRelOrderTransTypeValue = typeof StcIntRelOrderTransType[keyof typeof StcIntRelOrderTransType];

// Legacy put-away types (keep for backwards compatibility)
export interface PutAwayReservationDto {
  binCode: string;
  stcZoneID: string;
  resourceNo: number;
  stcBinGrp: number;
  resQuantity: number;
  quantity: number;
  pickableBinYesNo: boolean;
  dummy: boolean;
}

export interface CreatePutAwayRequest {
  companyNo: string;
  warehouseID: string;
  itemNo: string;
  binGrpNo: number;
  pickRuleNo: number;
  grShipmentNo: number;
  putAwayToPickableBinYesNo: boolean;
  quantity: number;
  // For Standard mode - direct receipt from POrder
  pOrderNo?: number;
  pOrderLineNo?: number;
}

// Location suggestion source enum - matching backend LocationSuggestionSource
export enum LocationSuggestionSource {
  None = 0,
  ErpPOrderLine = 1,
  ItemStdBinCode = 2,
  Calculated = 3,
  Placeholder = 4
}

export interface CreatePutAwayResponse {
  success: boolean;
  reservations: PutAwayReservationDto[];
  errorMessage?: string;
  isStandardMode: boolean;
  suggestedLocationSource: LocationSuggestionSource;
}

// New completion endpoint types (aligned with backend DTOs in CompletePutAwayDtos.cs)
export interface CompletePutAwayLineRequest {
  warehouseID: string;
  toBinCode: string;
  quantity: number;
}

export interface ItemBinsUpdateInfo {
  binCode: string;
  itemNo: string;
  quantityChange: number;
}

export interface CompletePutAwayLineResponse {
  success: boolean;
  errorMessage?: string;
  message?: string;
  updatedItemBins: ItemBinsUpdateInfo[];
  /** Mengde plassert i denne operasjonen */
  placedQuantity: number;
  /** Resterende mengde (undefined hvis full plassering) */
  remainingQuantity?: number;
  /** StcLineNo for ny restlinje ved split-plassering (undefined hvis full) */
  newLineStcLineNo?: number;
  /** true hvis hele mengden er plassert */
  isFullyPlaced: boolean;
}

export interface CompletePutAwayBulkRequest {
  warehouseID: string;
  toBinCode: string;
}

export interface CompletedLineInfo {
  stcLineNo: number;
  itemNo: string;
  quantity: number;
  fromBinCode: string;
  toBinCode: string;
}

export interface CompletePutAwayBulkResponse {
  success: boolean;
  errorMessage?: string;
  message?: string;
  completedCount: number;
  completedLines: CompletedLineInfo[];
}
